import { AxiosResponse } from 'axios';
import { DocumentDetails } from '../components/tasks/useTasks';
import { buildPath } from '../lib/url';
import { backendClient as client } from './clients';
import {
    CommentDTO,
    CommentUpdateDTO,
    PersonReferenceDTO,
    TaskAddAssigneesDTO,
    TaskAssignmentDTO,
    TaskAssignmentGroupDTO,
    TaskAssignmentSequenceDTO,
    TaskAssignmentTypeDTO,
    TaskDTO,
    TaskTypeDTO,
} from './dto';
import { ActionOne, CreateOne, GetManyPaged, GetManyUnpaged } from './types';

export type TaskIdentifier = { taskId: string; comment?: CommentUpdateDTO };

const TASKS_PATH = 'tasks';

export const getTasks: GetManyPaged<TaskPreciseDTO> = async ({ filter, page, size, sort, config } = {}) =>
    client
        .get(buildPath([TASKS_PATH]), { ...config, params: { filter, page, size, sort } })
        .then((response: AxiosResponse) => response.data);

export const addTaskAssignees: ActionOne<TaskIdentifier & { newAssignees: PersonReferenceDTO[] }, void> = async ({
    taskId,
    newAssignees,
    comment,
    config,
}) => {
    const body: TaskAddAssigneesDTO = { newAssignees, comment: comment ?? { comment: null } };
    return client
        .post(buildPath([TASKS_PATH, taskId, 'add']), body, config)
        .then((response: AxiosResponse) => response.data);
};

export const getTaskTypes: GetManyUnpaged<{}, TaskTypeDTO> = async ({ config } = {}) =>
    client.get(buildPath([TASKS_PATH, 'types']), config).then((response: AxiosResponse) => response.data);

export const getDocumentComments: GetManyUnpaged<{ data: DocumentDetails[] }, CommentDTO> = async ({ data, config }) =>
    client
        .get(buildPath([TASKS_PATH, 'comments']), {
            ...config,
            params: {
                documentTypes: data.map((d) => d.documentType).join(','),
                documentIds: data.map((d) => d.documentId).join(','),
            },
        })
        .then((response: AxiosResponse) => response.data);

export const addComment: CreateOne<{ data: CommentUpdateDTO; id: string }, CommentDTO> = async ({ data, id, config }) =>
    client
        .post(buildPath([TASKS_PATH, id, 'add-comment']), data, config)
        .then((response: AxiosResponse) => response.data);

export const updateTimeoutEscalation: ActionOne<{ taskId: string; date: Date | null }, void> = async ({
    taskId,
    date,
    config,
}) =>
    client
        .post(buildPath([TASKS_PATH, taskId, 'timeout-escalation']), { date }, config)
        .then((response: AxiosResponse) => response.data);

export const updateTimeoutReminder: ActionOne<{ taskId: string; date: Date | null }, void> = async ({
    taskId,
    date,
    config,
}) =>
    client
        .post(buildPath([TASKS_PATH, taskId, 'timeout-reminder']), { date }, config)
        .then((response: AxiosResponse) => response.data);

export interface TaskAssignmentSequencePreciseDTO extends TaskAssignmentSequenceDTO {
    groups: TaskAssignmentGroupPreciseDTO[];
}

export type TaskAssignmentGroupPreciseDTO = (
    | {
          sequence: true;
          assignments: [TaskAssignmentPreciseDTO];
      }
    | {
          sequence: false;
          assignments: TaskAssignmentPreciseDTO[];
      }
) &
    Omit<TaskAssignmentGroupDTO, 'sequence' | 'assignments'>;

export type TaskAssignmentPreciseDTO =
    | TaskAssignmentUnionDTO<TaskAssignmentTypeDTO.PERSON, 'label', 'label'>
    | TaskAssignmentUnionDTO<TaskAssignmentTypeDTO.EGROUP, 'egroup' | 'label', 'label'>
    | TaskAssignmentUnionDTO<TaskAssignmentTypeDTO.ROLE, 'role' | 'isImpactRole' | 'targets' | 'label', 'label'>
    | TaskAssignmentUnionDTO<TaskAssignmentTypeDTO.CUSTOM, 'custom'>;

export type TaskAssignmentUnionDTO<
    Type extends TaskAssignmentTypeDTO,
    Required extends keyof TaskAssignmentNoTypeDTO = never,
    Nullable extends Required = never
> = {
    type: Type;
} & NonNullableProps<TaskAssignmentNoTypeDTO, Exclude<Required, Nullable>> &
    Pick<TaskAssignmentNoTypeDTO, Nullable> &
    Record<Exclude<keyof TaskAssignmentNoTypeDTO, Required>, null>;

type TaskAssignmentNoTypeDTO = Omit<TaskAssignmentDTO, 'type'>;

type NonNullableProps<T, K extends keyof T> = {
    [P in K]: NonNullable<T[P]>;
};

export type TaskPreciseDTO = Omit<TaskDTO, 'assignment'> & { assignment: TaskAssignmentSequencePreciseDTO };
